import React, { useEffect, useState } from "react";
import { Box, Paper } from "@mui/material";
import {
  ClientIntegrationType,
  GetClientIntegrationDocument,
  IntegrationType,
  useUpdateClientIntegrationConfigurationMutation,
} from "~/operations";
import { useForm } from "react-hook-form";
import { Space } from "~/lib/types";
import { TicketIntegration } from "~/pages/tickets/components/TicketSettings/types";
import { ZendeskFormInput } from "~/pages/integrations/hosted-integrations/forms/zendesk/ZendeskIntegrationForm";
import { JiraAutoConfigurationSection } from "~/pages/integrations/hosted-integrations/forms/jira/JiraAutoConfigurationSection";
import { getIntegrationMrn } from "~/pages/integrations/utils/integrationLinksUtils";

type CaseSettingsIntegrationProps = {
  integration: TicketIntegration;
  space: Space;
  showAutoCreateSection: boolean;
};

export const TicketSettingsZendeskIntegration = ({
  integration,
  space,
  showAutoCreateSection,
}: CaseSettingsIntegrationProps) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const { control, handleSubmit, reset, watch } = useForm<ZendeskFormInput>({
    defaultValues: {
      autoCloseTickets: false,
      autoCreateTickets: false,
    },
    mode: "onChange",
  });

  const [updateIntegration] = useUpdateClientIntegrationConfigurationMutation({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    refetchQueries: [
      {
        query: GetClientIntegrationDocument,
        variables: {
          mrn: getIntegrationMrn({
            scope: space,
            integrationId: integration?.mrn.split("/").pop() || "",
          }),
        },
      },
    ],
  });

  const onSubmit = async (data: ZendeskFormInput) => {
    const zendeskConfigurationOptions = {
      subdomain: data.subdomain.trim(),
      email: data.email.trim(),
      apiToken: "",
      autoCloseTickets: data.autoCloseTickets,
      autoCreateTickets: data.autoCreateTickets,
    };

    await updateIntegration({
      variables: {
        input: {
          mrn: getIntegrationMrn({
            scope: space,
            integrationId: integration?.mrn.split("/").pop() || "",
          }),
          type: ClientIntegrationType.TicketSystemZendesk,
          configurationOptions: {
            zendeskConfigurationOptions,
          },
        },
      },
    });
  };

  useEffect(() => {
    // TypeScript users
    const subscription = watch(() => {
      isInitialized && handleSubmit(onSubmit)();
    });
    return () => subscription.unsubscribe();
  }, [handleSubmit, watch, isInitialized]);

  useEffect(() => {
    const configOptions = integration?.configurationOptions;
    if (configOptions?.__typename !== "ZendeskConfigurationOptions") return;
    reset({
      subdomain: configOptions.subdomain,
      email: configOptions.email,
      autoCloseTickets: configOptions.autoCloseTickets,
      autoCreateTickets: configOptions.autoCreateTickets,
    });
    setIsInitialized(true);
  }, []);

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <JiraAutoConfigurationSection
          control={control}
          ticketType={IntegrationType.TicketSystemZendesk}
          showAutoCreateSection={showAutoCreateSection}
        />
      </Paper>
    </Box>
  );
};
